<template>
    <div class="admin-section users-tab">
        <div class="users-tab__toolbar">
            <UsersFilters
                v-model:status="filters.status"
                v-model:role-code="filters.roleCode"
                v-model:partner-id="filters.partnerId"
                v-model:search-query="filters.searchQuery"
                :partners="partners"
                :status-counts="statusCounts"
                :show-admin-role="Boolean(actor.is_admin)"
            />
            <button
                v-if="actor.can_create_users"
                type="button"
                class="btn-primary users-tab__create"
                @click="openDrawer(DRAWER_MODE.CREATE)"
            >
                <span aria-hidden="true">+</span> Добавить пользователя
            </button>
        </div>

        <UserTree
            v-if="groups.length"
            :groups="groups"
            :is-filtered="isFiltered"
            :actor-id="actor.id"
            :partner-names="partnerNames"
            :advertiser-names="advertiserNames"
            :busy-user-id="busyUserId"
            @action="handleAction"
        />

        <div v-else class="users-tab__empty">
            <template v-if="!users.length">
                <p class="users-tab__empty-title">Пользователей пока нет</p>
                <p class="users-tab__empty-text">Создайте первого — он появится здесь вместе со своими доступами.</p>
            </template>
            <template v-else>
                <p class="users-tab__empty-title">Никого не нашлось</p>
                <p class="users-tab__empty-text">
                    Нет пользователей со статусом «{{ statusLabel }}»{{ isFiltered ? ' под выбранные фильтры' : '' }}.
                </p>
                <button v-if="isFiltered" type="button" class="btn-secondary" @click="resetFilters">Сбросить фильтры</button>
            </template>
        </div>

        <UserDrawer
            :open="drawer.open"
            :mode="drawer.mode"
            :user="drawer.user"
            :actor="actor"
            :users="users"
            :roles="roles"
            :partners="partners"
            :advertisers="advertisers"
            @close="closeDrawer"
            @saved="onDrawerSaved"
        />

        <ResetPasswordDrawer
            :open="passwordReset.open"
            :user="passwordReset.user"
            @close="passwordReset.open = false"
            @saved="onPasswordReset"
        />

        <ConfirmDialog
            :open="confirm.open"
            :title="confirmTitle"
            :confirm-label="confirm.action === USER_ACTION.DEACTIVATE ? 'Деактивировать' : 'Одобрить'"
            :tone="confirm.action === USER_ACTION.DEACTIVATE ? 'danger' : 'primary'"
            :busy="confirm.busy"
            @confirm="runConfirmedAction"
            @cancel="closeConfirm"
        >
            <template v-if="confirm.action === USER_ACTION.DEACTIVATE && confirm.user">
                <p><strong>{{ confirm.user.email }}</strong> больше не сможет войти.</p>
                <ul>
                    <li>Все доступы и пакеты экранов будут сняты.</li>
                    <li v-if="confirmChildrenCount">
                        Подчинённые ({{ pluralUsers(confirmChildrenCount) }}) перейдут под ваше управление и сохранят свои доступы.
                    </li>
                    <li>Креативы и кампании сохранятся.</li>
                </ul>
                <p>Пользователя можно будет восстановить из вкладки «Неактивные».</p>
            </template>
            <template v-else-if="confirm.user">
                <p>Одобрить регистрацию <strong>{{ confirm.user.email }}</strong>? Пользователь получит доступ к платформе.</p>
            </template>
        </ConfirmDialog>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import axios from 'axios';
import UsersFilters from './users/UsersFilters.vue';
import UserTree from './users/UserTree.vue';
import UserDrawer from './users/UserDrawer.vue';
import ResetPasswordDrawer from './users/ResetPasswordDrawer.vue';
import ConfirmDialog from '../ui/ConfirmDialog.vue';
import { buildUserTree, childrenOf } from './users/buildUserTree';
import { DRAWER_MODE, USER_ACTION, USER_STATUS, USER_STATUS_OPTIONS, userStatusOf } from '../../constants/users';
import { useToast } from '../../composables/useToast';
import { extractErrorMessage } from '../../utils/httpErrors';
import { PLURAL, pluralize } from '../../utils/plural';

const props = defineProps({
    actor: { type: Object, required: true },
    roles: { type: Array, required: true },
    partners: { type: Array, required: true },
    advertisers: { type: Array, required: true },
    /** Предустановленные фильтры при переходе из другой вкладки, например { partnerId } из «Структуры». */
    initialFilters: { type: Object, default: () => ({}) },
});

const users = defineModel('users', { type: Array, required: true });

const toast = useToast();

const filters = reactive({
    status: USER_STATUS.ACTIVE,
    roleCode: '',
    partnerId: props.initialFilters.partnerId ? String(props.initialFilters.partnerId) : '',
    searchQuery: '',
});

const partnerNames = computed(() => new Map(props.partners.map(partner => [Number(partner.id), partner.name])));
const advertiserNames = computed(() => new Map(props.advertisers.map(advertiser => [Number(advertiser.id), advertiser.name])));
const advertiserPartnerIds = computed(() => new Map(props.advertisers.map(advertiser => [
    Number(advertiser.id),
    advertiser.partner_id == null ? null : Number(advertiser.partner_id),
])));

const isFiltered = computed(() => Boolean(filters.roleCode || filters.partnerId || filters.searchQuery));

const belongsToPartner = (user, partnerId) => {
    const scope = user.scope || {};
    if ((scope.partner_ids || []).some(id => Number(id) === partnerId)) return true;
    return (scope.advertiser_ids || []).some(id => advertiserPartnerIds.value.get(Number(id)) === partnerId);
};

const matchesFilters = (user) => {
    if (filters.roleCode && user.role?.code !== filters.roleCode) return false;
    if (filters.partnerId && !belongsToPartner(user, Number(filters.partnerId))) return false;
    if (filters.searchQuery && !user.email.toLowerCase().includes(filters.searchQuery.toLowerCase())) return false;
    return true;
};

const statusCounts = computed(() => users.value.filter(matchesFilters).reduce((counts, user) => {
    const status = userStatusOf(user);
    counts[status] = (counts[status] || 0) + 1;
    return counts;
}, {}));

const groups = computed(() => buildUserTree(
    users.value,
    user => userStatusOf(user) === filters.status && matchesFilters(user),
));

const statusLabel = computed(() => USER_STATUS_OPTIONS.find(option => option.value === filters.status)?.label.toLowerCase());

const resetFilters = () => {
    filters.roleCode = '';
    filters.partnerId = '';
    filters.searchQuery = '';
};

const pluralUsers = count => `${count} ${pluralize(count, PLURAL.USER)}`;

// ===== Состояние списка =====

const replaceUser = (updated) => {
    users.value = users.value.some(user => user.id === updated.id)
        ? users.value.map(user => (user.id === updated.id ? updated : user))
        : [...users.value, updated];
};

const applyReassignment = (reassignedIds = []) => {
    if (!reassignedIds.length) return;
    const ids = new Set(reassignedIds.map(Number));
    users.value = users.value.map(user => (ids.has(user.id) ? { ...user, created_by: props.actor.id } : user));
};

// ===== Боковая панель =====

const drawer = reactive({ open: false, mode: DRAWER_MODE.CREATE, user: null });
const passwordReset = reactive({ open: false, user: null });

const openDrawer = (mode, user = null) => {
    drawer.mode = mode;
    drawer.user = user;
    drawer.open = true;
};

const closeDrawer = () => {
    drawer.open = false;
};

const onPasswordReset = ({ message }) => {
    toast.success(message);
};

const onDrawerSaved = ({ user, reassignedIds, message }) => {
    replaceUser(user);
    applyReassignment(reassignedIds);
    closeDrawer();

    if (userStatusOf(user) !== filters.status) filters.status = userStatusOf(user);
    toast.success(reassignedIds?.length
        ? `${message}. Вам переподчинено: ${pluralUsers(reassignedIds.length)}.`
        : message);
};

// ===== Подтверждения =====

const confirm = reactive({ open: false, action: null, user: null, busy: false });
const busyUserId = ref(null);

const confirmTitle = computed(() => (confirm.action === USER_ACTION.DEACTIVATE
    ? 'Деактивировать пользователя?'
    : 'Одобрить регистрацию?'));

const confirmChildrenCount = computed(() => (confirm.user ? childrenOf(users.value, confirm.user.id).length : 0));

const openConfirm = (action, user) => {
    Object.assign(confirm, { open: true, action, user, busy: false });
};

const closeConfirm = () => {
    confirm.open = false;
};

const runConfirmedAction = async () => {
    const { action, user } = confirm;
    confirm.busy = true;
    busyUserId.value = user.id;

    try {
        if (action === USER_ACTION.DEACTIVATE) {
            const { data } = await axios.delete(`/administration/users/${user.id}`);
            replaceUser(data.user);
            applyReassignment(data.reassigned_user_ids);
            toast.success(data.reassigned_user_ids?.length
                ? `${user.email} деактивирован. Вам переподчинено: ${pluralUsers(data.reassigned_user_ids.length)}.`
                : `${user.email} деактивирован`);
        } else {
            const { data } = await axios.post(`/administration/users/${user.id}/approve`);
            replaceUser(data.user);
            toast.success(`${user.email} одобрен`);
        }
        closeConfirm();
    } catch (error) {
        toast.error(extractErrorMessage(error));
    } finally {
        confirm.busy = false;
        busyUserId.value = null;
    }
};

const handleAction = (action, user) => {
    if (action === USER_ACTION.EDIT) openDrawer(DRAWER_MODE.EDIT, user);
    else if (action === USER_ACTION.RESTORE) openDrawer(DRAWER_MODE.RESTORE, user);
    else if (action === USER_ACTION.RESET_PASSWORD) Object.assign(passwordReset, { open: true, user });
    else openConfirm(action, user);
};
</script>

<style scoped lang="scss">
@use '../../../scss/pages/admin/admin-tokens' as *;

.users-tab__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: $spacing-lg;
    margin-bottom: $spacing-xl;
}

.users-tab__create {
    flex: 0 0 auto;
    white-space: nowrap;
}

.users-tab__empty {
    padding: $spacing-2xl;
    border: 1px dashed $admin-border-hover;
    border-radius: $radius-lg;
    text-align: center;
    background: $admin-bg-primary;
}

.users-tab__empty-title {
    margin: 0 0 $spacing-xs;
    font-size: $font-size-lg;
    font-weight: 600;
    color: $admin-text-primary;
}

.users-tab__empty-text {
    margin: 0 0 $spacing-lg;
    color: $admin-text-muted;
}
</style>
